/**
 * The survey BUILDER form model (SurveyFormDialog): flat form values ↔ the
 * shared wire body (`createSurveySchema` / `updateSurveySchema`).
 *
 * Every question is edited in one flat "superset" shape (all per-type fields
 * present) so switching a question's type never loses what was typed; the
 * wire builder keeps only the fields the chosen type has. The flat field
 * names match the wire names, so a shared-schema issue path
 * (`questions.2.options.0.label`) maps straight onto the form field.
 *
 * Validation is the SHARED schema (one validation path): `surveyBuilderResolver`
 * runs `createSurveySchema` over the converted body and returns RHF errors.
 */
import type { FieldErrors, Resolver } from 'react-hook-form';
import {
  SURVEY_INVALID_WEBSITE_URL,
  createSurveySchema,
  type CreateSurveyInput,
  type SurveyDisplayMode,
  type SurveyOption,
  type SurveyQuestion,
  type SurveyQuestionInput,
  type SurveyQuestionType,
  type SurveyWire,
  type UpdateSurveyInput,
} from '@releve/shared';
import type { SurveyTranslate } from './surveyLabels';

export interface BuilderOption {
  id: string;
  label: string;
  /** Volunteer-signup intake only: an answered option retired instead of removed (carried, never dropped). */
  retired?: boolean;
}

export interface BuilderQuestion {
  id: string;
  type: SurveyQuestionType;
  label: string;
  help: string;
  required: boolean;
  options: BuilderOption[];
  rows: BuilderOption[];
  columns: BuilderOption[];
  /** scale: 0 | 1; number: lower bound or '' */
  min: number | '';
  /** scale / rating: top value; number: upper bound or '' */
  max: number | '';
  min_label: string;
  max_label: string;
  min_selections: number | '';
  max_selections: number | '';
  integer: boolean;
  /**
   * Volunteer-signup intake only: the answer is sensitive (Art. 9) and stored
   * apart. Defaults to false; `toWireQuestion` never emits it, so a survey
   * body (`toCreateSurveyBody`) never carries it.
   */
  sensitive: boolean;
  /**
   * Volunteer-signup intake only: an answered question retired instead of
   * deleted (hidden from new answers, kept for existing ones). Carried through
   * the round-trip like `sensitive`, so a save never un-retires; emitted only
   * when true, so a survey body never carries it.
   */
  retired: boolean;
}

export interface SurveyBuilderValues {
  title: string;
  description: string;
  /** The respondent's thank-you text ('' → the default sentence). Editable after opening. */
  thank_you_message: string;
  /**
   * The "Visit {org}" button link override ('' → the org's website). Checked
   * by the shared normaliser (via `createSurveySchema`); editable after opening.
   */
  website_url: string;
  /** The website button's own text ('' → "Visit {hostname}" / "Visit {org}"). Editable after opening. */
  website_label: string;
  /** The line above the title ('' → the default, `surveyIntroLine`). Editable after opening. */
  intro_line: string;
  /** The public page layout. Editable after opening. */
  display_mode: SurveyDisplayMode;
  project_id: string;
  questions: BuilderQuestion[];
}

const newId = () => crypto.randomUUID();

export const newOption = (): BuilderOption => ({ id: newId(), label: '' });

/** A blank question of `type` with sensible per-type defaults. */
export function newBuilderQuestion(type: SurveyQuestionType = 'short_text'): BuilderQuestion {
  return {
    id: newId(),
    type,
    label: '',
    help: '',
    required: false,
    options: [newOption(), newOption()],
    rows: [newOption()],
    columns: [newOption(), newOption()],
    ...typeDefaults(type),
    min_label: '',
    max_label: '',
    min_selections: '',
    max_selections: '',
    integer: false,
    sensitive: false,
    retired: false,
  };
}

/** The `min` / `max` a type starts from when the user switches to it. */
export function typeDefaults(type: SurveyQuestionType): Pick<BuilderQuestion, 'min' | 'max'> {
  if (type === 'scale') return { min: 1, max: 5 };
  if (type === 'rating') return { min: '', max: 5 };
  return { min: '', max: '' };
}

export const emptySurveyBuilderValues = (): SurveyBuilderValues => ({
  title: '',
  description: '',
  thank_you_message: '',
  website_url: '',
  website_label: '',
  intro_line: '',
  display_mode: 'all',
  project_id: '',
  questions: [newBuilderQuestion()],
});

/** `{id, label}` plus `retired: true` when set (never `retired: false`). */
const optionOf = (o: { id: string; label: string; retired?: boolean }): BuilderOption =>
  o.retired === true ? { id: o.id, label: o.label, retired: true } : { id: o.id, label: o.label };

const copyOptions = (items: readonly SurveyOption[] | undefined): BuilderOption[] =>
  items && items.length > 0 ? items.map(optionOf) : [];

/** A stored question → the flat builder shape. */
export function toBuilderQuestion(q: SurveyQuestion): BuilderQuestion {
  const base = newBuilderQuestion(q.type);
  const next: BuilderQuestion = {
    ...base,
    id: q.id,
    label: q.label,
    help: q.help ?? '',
    required: q.required ?? false,
    sensitive: q.type !== 'section' && (q.sensitive ?? false),
    retired: q.retired === true,
  };
  switch (q.type) {
    case 'single_choice':
    case 'dropdown':
      return { ...next, options: copyOptions(q.options) };
    case 'multi_choice':
      return {
        ...next,
        options: copyOptions(q.options),
        min_selections: q.min_selections ?? '',
        max_selections: q.max_selections ?? '',
      };
    case 'scale':
      return { ...next, min: q.min, max: q.max, min_label: q.min_label ?? '', max_label: q.max_label ?? '' };
    case 'rating':
      return { ...next, max: q.max };
    case 'choice_grid':
    case 'checkbox_grid':
      return { ...next, rows: copyOptions(q.rows), columns: copyOptions(q.columns) };
    case 'number':
      return { ...next, min: q.min ?? '', max: q.max ?? '', integer: q.integer ?? false };
    default:
      return next;
  }
}

export function surveyToBuilderValues(survey: SurveyWire): SurveyBuilderValues {
  return {
    title: survey.title,
    description: survey.description ?? '',
    thank_you_message: survey.thank_you_message ?? '',
    website_url: survey.website_url ?? '',
    website_label: survey.website_label ?? '',
    intro_line: survey.intro_line ?? '',
    display_mode: survey.display_mode,
    project_id: survey.project_id ?? '',
    questions: survey.questions.map(toBuilderQuestion),
  };
}

const orNull = (value: number | '') => (value === '' ? null : value);
const orUndefinedNumber = (value: number | '') => (value === '' ? undefined : value);

/** The flat builder question → the wire question for its type (only that type's keys). */
export function toWireQuestion(q: BuilderQuestion): SurveyQuestionInput {
  const retired = q.retired ? { retired: true } : {};
  const common = { id: q.id, label: q.label, help: q.help, required: q.required, ...retired };
  const options = q.options.map(optionOf);
  switch (q.type) {
    case 'section':
      return { id: q.id, type: 'section', label: q.label, help: q.help, required: false, ...retired };
    case 'single_choice':
    case 'dropdown':
      return { ...common, type: q.type, options };
    case 'multi_choice':
      return {
        ...common,
        type: 'multi_choice',
        options,
        min_selections: orNull(q.min_selections),
        max_selections: orNull(q.max_selections),
      };
    case 'scale':
      return {
        ...common,
        type: 'scale',
        min: (q.min === 0 ? 0 : 1) as 0 | 1,
        max: orUndefinedNumber(q.max) as number,
        min_label: q.min_label,
        max_label: q.max_label,
      };
    case 'rating':
      return { ...common, type: 'rating', max: orUndefinedNumber(q.max) as number };
    case 'choice_grid':
    case 'checkbox_grid':
      return {
        ...common,
        type: q.type,
        rows: q.rows.map(optionOf),
        columns: q.columns.map(optionOf),
      };
    case 'number':
      return { ...common, type: 'number', min: orNull(q.min), max: orNull(q.max), integer: q.integer };
    default:
      return { ...common, type: q.type };
  }
}

/** The create body. `.strict()` on the server — only the schema's keys. */
export function toCreateSurveyBody(values: SurveyBuilderValues): CreateSurveyInput {
  return {
    title: values.title,
    description: values.description,
    thank_you_message: values.thank_you_message,
    website_url: values.website_url,
    website_label: values.website_label,
    intro_line: values.intro_line,
    display_mode: values.display_mode,
    project_id: values.project_id || null,
    questions: values.questions.map(toWireQuestion),
  } as unknown as CreateSurveyInput;
}

/**
 * The PATCH body. A survey that has left `draft` has frozen questions (the
 * DB guard refuses a change → 409), so `questions` is sent only for a draft.
 * Everything else — `intro_line` and `display_mode` included — is sent
 * either way (not frozen).
 */
export function toUpdateSurveyBody(values: SurveyBuilderValues, isDraft: boolean): UpdateSurveyInput {
  const body = toCreateSurveyBody(values) as unknown as Record<string, unknown>;
  if (!isDraft) delete body.questions;
  return body as unknown as UpdateSurveyInput;
}

/** Shared-schema issue → a short translated message. */
function issueMessage(path: (string | number)[], code: string, message: string, t: SurveyTranslate): string {
  const last = path[path.length - 1];
  if (message === SURVEY_INVALID_WEBSITE_URL) return t('surveys:dialog.errors.websiteUrl');
  if (path.length === 1 && path[0] === 'questions') {
    return t('surveys:dialog.errors.questions');
  }
  if (last === 'options' || last === 'rows' || last === 'columns') return t('surveys:dialog.errors.options');
  if (last === 'min' || last === 'min_selections') return t('surveys:dialog.errors.range');
  if (code === 'too_small' || last === 'label' || last === 'title') return t('surveys:dialog.errors.required');
  return t('surveys:dialog.errors.invalid');
}

/**
 * The builder's resolver: the SHARED `createSurveySchema` over the converted
 * body. Issue paths are the wire paths, which the flat form mirrors.
 * The resolver returns the RAW form values on success (the dialog converts
 * them on submit), so no transformed text ever lands back in the form.
 */
export function surveyBuilderResolver(t: SurveyTranslate): Resolver<SurveyBuilderValues> {
  return async (values) => {
    const result = createSurveySchema.safeParse(toCreateSurveyBody(values));
    if (result.success) return { values, errors: {} };
    const errors: Record<string, unknown> = {};
    for (const issue of result.error.issues) {
      const path = issue.path.length > 0 ? issue.path : ['questions'];
      // Arrays (options/rows/columns/questions) carry their message on `root`.
      const key = path.join('.');
      if (getIn(errors, key)) continue;
      setIn(errors, key, { type: issue.code, message: issueMessage(path, issue.code, issue.message, t) });
    }
    return { values: {}, errors: errors as FieldErrors<SurveyBuilderValues> };
  };
}

function getIn(target: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>((node, key) => (node && typeof node === 'object' ? (node as Record<string, unknown>)[key] : undefined), target);
}

function setIn(target: Record<string, unknown>, path: string, value: unknown): void {
  const keys = path.split('.');
  let node: Record<string, unknown> = target;
  keys.slice(0, -1).forEach((key, i) => {
    const next = node[key];
    if (!next || typeof next !== 'object') {
      node[key] = /^\d+$/.test(keys[i + 1]) ? [] : {};
    }
    node = node[key] as Record<string, unknown>;
  });
  const last = keys[keys.length - 1];
  if (node[last] && typeof node[last] === 'object') return; // a deeper error already sits here
  node[last] = value;
}
